import {
	Center,
	Flex,
	Group,
	Menu,
	Stack,
	UnstyledButton,
	createStyles,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import {
	capitalize,
	escapeRegExp,
	filter,
	isEmpty,
	isNil,
	isString,
	map,
} from 'lodash-es';
import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router';

import MarkdownRenderer from '@repo/common/components/MarkdownRenderer';
import { getSummaryAndBreadCrumbs } from '@repo/common/utils/breadcrumb';
import { Button, Icon, IconButton, Text, Title } from '@repo/foundations';
import dayjs from 'dayjs';

import { useApiDeleteAnnouncement } from '@repo/api-codegen';
import { useCurrentUser, useUser } from '../../../../../../../api';
import { useNotificationList } from '../../../../../../../api/hooks/notification';
import { useSecodaEntityList } from '../../../../../../../api/hooks/secodaEntity';
import type { INotification } from '../../../../../../../api/types/models/notification';
import AnnouncementModal from '../../../../../../../components/Announcement/AnnouncementModal';
import { ScrollableModal } from '../../../../../../../components/ScrollableModal/ScrollableModal';
import { SecodaEntityIcon } from '../../../../../../../components/SecodaEntity/SecodaEntityIcon';
import { RelativeTimeRender } from '../../../../../../../components/TableV2/render';
import { UserAvatar } from '../../../../../../../components/UserAvatar';
import { entityUrl } from '../../../../../../../utils/navigationUtils';
import { WIDGET_CONTENT_HEIGHT } from '../../../constants';

const useStyles = createStyles((theme) => ({
	emptyState: {
		height: WIDGET_CONTENT_HEIGHT,
		flexDirection: 'column',
		gap: theme.spacing.md,
	},
	markdown: {
		color: theme.fn.themeColor('gray', 6),
		fontSize: theme.fontSizes.sm,
		'& p': {
			margin: 0,
			padding: 0,
		},
		'& > *:first-of-type': {
			marginTop: 0,
		},
		'& > *:last-child': {
			marginBottom: 0,
		},
	},
	container: {
		display: 'flex',
		alignItems: 'center',

		gap: theme.spacing.sm,
		padding: theme.spacing.xs,
		borderRadius: theme.other.space[2],

		'&:hover, &:active': {
			backgroundColor: theme.other.getColor('surface/primary/hover'),
		},
	},
	label: {
		fontSize: theme.fontSizes.sm,
		fontWeight: theme.other.typography.weight.semibold,
		lineHeight: '20px',
		color: theme.other.getColor('text/primary/default'),
	},
	wrapper: {
		padding: `${theme.spacing.sm} ${theme.spacing.sm} ${theme.spacing.sm} ${theme.spacing.md}`,
		borderRadius: theme.radius.md,
		boxShadow: theme.shadows.sm,
		backgroundColor: theme.other.getColor('surface/primary/default'),
		width: '100%',
	},
}));

function extractAnnouncementBody(content: string, definition: string): string {
	// In the backend, we store announcement heading (content) and body together in definition
	// Separating these to get the body
	const headingRegex = new RegExp(
		`^#\\s*${escapeRegExp(content)}\\s*\\n+`,
		'i'
	);
	return definition.replace(headingRegex, '');
}

function AnnoucementUser({ notification }: { notification: INotification }) {
	const { classes } = useStyles();
	const { data: user } = useUser({
		id: notification.sender.id,
		options: {
			enabled: !isNil(notification.sender?.id),
		},
	});
	const { content, metadata, created_at } = notification;
	const timeAgo = dayjs(created_at).fromNow(false);
	return (
		<>
			{user && <UserAvatar size="md" user={user} />}
			<Flex direction="column" w="100%">
				<Text
					size="sm"
					color="text/primary/default"
					weight="bold"
					style={{ whiteSpace: 'unset' }}
					lineClamp={1}
				>
					{capitalize(content)}
				</Text>
				<Flex gap="3xs">
					<Text
						size="xs"
						color="text/secondary/default"
						style={{ whiteSpace: 'unset', flex: 1, minWidth: 0 }}
						lineClamp={1}
					>
						<ReactMarkdown className={classes.markdown}>
							{extractAnnouncementBody(
								content,
								isString(metadata?.definition) ? metadata.definition : ''
							)}
						</ReactMarkdown>
					</Text>
					<Text
						size="xs"
						color="text/secondary/default"
						ml="auto"
						style={{ flexShrink: 0 }}
					>
						{timeAgo}
					</Text>
				</Flex>
			</Flex>
		</>
	);
}

function AnnouncementWidget() {
	const { classes } = useStyles();
	const [displayModalOpen, setDisplayModalOpen] = useState(false);
	const [selectedNotification, setSelectedNotification] =
		useState<INotification | null>(null);
	const [editModalOpen, setEditModalOpen] = useState(false);
	const queryClient = useQueryClient();
	const { data: currentUser } = useCurrentUser();

	const { mutate: deleteAnnouncement } = useApiDeleteAnnouncement({
		onSuccess: () => {
			showNotification({
				message: 'Announcement Deleted',
				color: 'green',
			});
			setDisplayModalOpen(false);
			queryClient.invalidateQueries({
				queryKey: ['notification', 'notifications'],
			});
		},
		onError: () => {
			showNotification({
				message: 'Error deleting announcement',
				color: 'red',
			});
		},
	});

	const { isFetched, data: notifications } = useNotificationList({
		filters: {
			distinct: 'dispatch_id',
			include_myself: true,
		},
		options: {
			select: ({ results }) =>
				filter(results, { event: 'announcement', archived: false }),
		},
	});

	const { data: relatedResources, refetch: refetchRelatedResources } =
		useSecodaEntityList({
			filters: {
				id__in: selectedNotification?.related_resources || [],
			},
			options: {
				enabled: !!selectedNotification?.related_resources?.length,
			},
		});

	useEffect(() => {
		refetchRelatedResources();
	}, [selectedNotification, refetchRelatedResources]);

	if (isFetched && isEmpty(notifications)) {
		return (
			<Center className={classes.emptyState}>
				<Icon name="speakerphone" size="lg" color="icon/primary/default" />
				<Stack spacing="none">
					<Text
						size="sm"
						weight="bold"
						align="center"
						color="text/secondary/default"
					>
						No new announcements
					</Text>
					<Text size="sm" align="center" color="text/secondary/default">
						Check back later for updates
					</Text>
				</Stack>
			</Center>
		);
	}

	const handleAnnouncementClick = (notification: INotification) => {
		setSelectedNotification(notification);
		setDisplayModalOpen(true);
	};

	const handleEditClick = () => {
		setDisplayModalOpen(false);
		setEditModalOpen(true);
	};

	const handleDeleteClick = () => {
		if (selectedNotification?.id) {
			deleteAnnouncement({
				body: {
					notification_id: selectedNotification.id,
					announcement_id: selectedNotification.resource_id,
				},
			});
		}
	};

	const announcementBody = extractAnnouncementBody(
		selectedNotification?.content ?? '',
		isString(selectedNotification?.metadata?.definition)
			? selectedNotification?.metadata?.definition
			: ''
	);

	// Prepare initial data for edit modal
	const editInitialData = selectedNotification
		? {
				notification_id: selectedNotification.id,
				announcement_id: selectedNotification.resource_id,
				subject: selectedNotification.content,
				definition: isString(selectedNotification.metadata?.definition)
					? extractAnnouncementBody(
							selectedNotification.content,
							selectedNotification.metadata.definition
						)
					: '',
				recipients: selectedNotification.receivers || [],
				receiver_slack_channel_ids:
					selectedNotification.receiver_slack_channel_ids || [],
				related_resources: relatedResources?.results ?? [],
			}
		: undefined;

	return (
		<Stack spacing={0} px="sm">
			{map(notifications, (notification) => (
				<UnstyledButton
					key={notification.id}
					className={classes.container}
					onClick={() => handleAnnouncementClick(notification)}
				>
					<AnnoucementUser notification={notification} />
				</UnstyledButton>
			))}
			<ScrollableModal
				opened={displayModalOpen}
				onClose={() => setDisplayModalOpen(false)}
				title={
					<Flex justify="space-between" align="center" w="100%">
						<Text color="text/primary/default" weight="bold">
							Announcement
						</Text>
						{currentUser?.id === selectedNotification?.sender?.id && (
							<Group spacing="xs">
								<Button onClick={handleEditClick}>Edit</Button>
								<Menu position="bottom-end">
									<Menu.Target>
										<IconButton iconName="dots" variant="tertiary" />
									</Menu.Target>
									<Menu.Dropdown>
										<Menu.Item onClick={handleDeleteClick}>Delete...</Menu.Item>
									</Menu.Dropdown>
								</Menu>
							</Group>
						)}
					</Flex>
				}
				size="lg"
				withTopPadding
			>
				{selectedNotification && (
					<Stack spacing="md">
						<Group spacing="sm">
							<UserAvatar size="xxs" user={selectedNotification.sender} />
							<Group spacing="3xs">
								<Text className={classes.label}>
									{selectedNotification.sender.display_name}
								</Text>
								<Text color="text/secondary/default">·</Text>
								<RelativeTimeRender
									record={selectedNotification}
									field="created_at"
									color="text/secondary/default"
								/>
							</Group>
						</Group>
						<Stack spacing="xs">
							<Title order={2}>{selectedNotification.content}</Title>
							<MarkdownRenderer>{announcementBody}</MarkdownRenderer>
						</Stack>
						{relatedResources &&
							relatedResources.results &&
							relatedResources.results.length > 0 && (
								<Stack spacing="sm" key={selectedNotification?.id}>
									<Text size="sm" color="text/secondary/default">
										Related Resources...
									</Text>
									<Stack spacing="xs">
										{map(relatedResources.results, (resource) => (
											<UnstyledButton
												key={resource.id}
												component={Link}
												to={entityUrl(resource)}
											>
												<Group spacing="sm" noWrap className={classes.wrapper}>
													<SecodaEntityIcon entity={resource} size={20} />
													<Stack spacing={0} miw={0}>
														<Text size="sm" weight="semibold">
															{resource.title}
														</Text>
														<Text
															inline
															size="xs"
															color="text/secondary/default"
															truncate
														>
															{getSummaryAndBreadCrumbs(
																resource.native_type ||
																	resource.entity_type ||
																	null,
																resource.search_metadata
															)}
														</Text>
													</Stack>
												</Group>
											</UnstyledButton>
										))}
									</Stack>
								</Stack>
							)}
					</Stack>
				)}
			</ScrollableModal>
			<AnnouncementModal
				key={selectedNotification?.id || 'create'}
				opened={editModalOpen}
				close={() => setEditModalOpen(false)}
				mode="edit"
				initialData={editInitialData}
			/>
		</Stack>
	);
}

export default AnnouncementWidget;
